import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

interface CompanyData {
  companyName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

const Terms: React.FC = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const data = await apiRequest<CompanyData>(API_ENDPOINTS.COMPANY.PUBLIC);
        setCompanyData(data);
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCompanyInfo();
  }, []);
  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Please read these terms carefully before using our services or making purchases.
            </p>
            <p className="text-sm text-neutral-500 mt-4">Last updated: December 2024</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-neutral-700 leading-relaxed">
                Welcome to {companyData?.companyName || 'Demo Crackers'}! These Terms of Service ("Terms") govern your use of our website and services. 
                By accessing or using our services, you agree to be bound by these Terms. If you disagree with any 
                part of these terms, then you may not access the service.
              </p>
            </section>

            {/* Age Requirements */}
            <section className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-red-800 mb-4 flex items-center">
                <span className="mr-3">🔞</span>
                Age Requirements
              </h2>
              <div className="text-red-700 space-y-3">
                <p className="font-semibold">IMPORTANT: You must be at least 18 years old to purchase fireworks.</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Valid government-issued ID required for delivery</li>
                  <li>Adult signature required for all fireworks orders</li>
                  <li>We reserve the right to refuse sale to minors</li>
                  <li>Providing false age information is prohibited</li>
                </ul>
              </div>
            </section>

            {/* Account Terms */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">👤</span>
                Account Terms
              </h2>
              <div className="space-y-4">
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-semibold text-neutral-900 mb-2">Account Creation</h3>
                  <ul className="text-neutral-700 space-y-1">
                    <li>• You must provide accurate and complete information</li>
                    <li>• You are responsible for maintaining account security</li>
                    <li>• One account per person/entity</li>
                    <li>• You must notify us of any unauthorized use</li>
                  </ul>
                </div>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-semibold text-neutral-900 mb-2">Account Responsibilities</h3>
                  <ul className="text-neutral-700 space-y-1">
                    <li>• Maintain confidentiality of login credentials</li>
                    <li>• Use account only for lawful purposes</li>
                    <li>• Comply with all applicable laws and regulations</li>
                    <li>• Respect intellectual property rights</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Product Terms */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🎆</span>
                Fireworks & Product Terms
              </h2>
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">Legal Compliance</h3>
                  <ul className="space-y-2 text-yellow-700">
                    <li>• All products comply with Indian fireworks regulations</li>
                    <li>• Some products may be restricted in certain areas</li>
                    <li>• Buyer responsible for checking local laws and regulations</li>
                    <li>• Use only as intended and follow all safety instructions</li>
                  </ul>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-2">Product Quality</h3>
                    <ul className="text-blue-700 text-sm space-y-1">
                      <li>• All products tested for quality and safety</li>
                      <li>• Licensed and certified products only</li>
                      <li>• Proper storage and handling during transit</li>
                      <li>• Fresh stock with valid shelf life</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 mb-2">Safety Requirements</h3>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• Must be used by adults or under supervision</li>
                      <li>• Follow all safety instructions provided</li>
                      <li>• Use in open, safe areas only</li>
                      <li>• Keep away from flammable materials</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Orders & Payment */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">💳</span>
                Orders & Payment
              </h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Order Processing</h3>
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Orders subject to acceptance and availability</li>
                      <li>• We reserve right to refuse or cancel orders</li>
                      <li>• Prices subject to change without notice</li>
                      <li>• Order confirmation does not guarantee acceptance</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Payment Terms</h3>
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Payment required at time of order</li>
                      <li>• All prices include applicable taxes</li>
                      <li>• Secure payment processing</li>
                      <li>• Refunds processed per our refund policy</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Shipping & Delivery */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🚚</span>
                Shipping & Delivery
              </h2>
              <div className="bg-neutral-100 rounded-xl p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Delivery Terms</h3>
                    <ul className="text-neutral-700 space-y-1">
                      <li>• Delivery timelines are estimates</li>
                      <li>• Adult signature required for all deliveries</li>
                      <li>• Special handling for fireworks shipments</li>
                      <li>• Risk of loss transfers upon delivery</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Shipping Restrictions</h3>
                    <ul className="text-neutral-700 space-y-1">
                      <li>• Some areas may have delivery restrictions</li>
                      <li>• Weather-dependent delivery for safety</li>
                      <li>• Cannot ship to PO boxes</li>
                      <li>• Special permits may be required</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Prohibited Uses */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🚫</span>
                Prohibited Uses
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <p className="text-red-800 font-semibold mb-3">You may not use our service:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-red-700">
                    <li>• For any unlawful purpose or activity</li>
                    <li>• To violate any laws or regulations</li>
                    <li>• To resell products without authorization</li>
                    <li>• To harm, threaten, or harass others</li>
                  </ul>
                  <ul className="space-y-2 text-red-700">
                    <li>• To transmit spam or malicious content</li>
                    <li>• To infringe on intellectual property</li>
                    <li>• To collect user information</li>
                    <li>• For commercial use without permission</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">©️</span>
                Intellectual Property
              </h2>
              <div className="space-y-4">
                <p className="text-neutral-700">
                  Our service and its original content, features, and functionality are owned by {companyData?.companyName || 'Demo Crackers'} 
                  and are protected by international copyright, trademark, patent, trade secret, and other 
                  intellectual property laws.
                </p>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-semibold text-neutral-900 mb-2">Your License</h3>
                  <p className="text-neutral-700 text-sm">
                    We grant you a limited, non-exclusive, non-transferable license to access and use our 
                    service for personal, non-commercial purposes in accordance with these Terms.
                  </p>
                </div>
              </div>
            </section>

            {/* Disclaimers */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">⚠️</span>
                Disclaimers & Liability
              </h2>
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">Product Disclaimer</h3>
                  <p className="text-yellow-700">
                    Fireworks are inherently dangerous products. Use at your own risk. We provide products 
                    with safety instructions, but cannot control how they are used. Users assume all 
                    responsibility for safe and legal use.
                  </p>
                </div>
                
                <div className="bg-neutral-100 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Limitation of Liability</h3>
                  <p className="text-neutral-700 mb-3">
                    To the maximum extent permitted by law, {companyData?.companyName || 'Demo Crackers'} shall not be liable for any indirect, 
                    incidental, special, consequential, or punitive damages.
                  </p>
                  <ul className="space-y-1 text-neutral-700">
                    <li>• Property damage or personal injury from product use</li>
                    <li>• Lost profits or business interruption</li>
                    <li>• Data loss or corruption</li>
                    <li>• Third-party claims or damages</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔚</span>
                Termination
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-semibold text-neutral-900 mb-2">We may terminate your access if:</h3>
                  <ul className="text-neutral-700 text-sm space-y-1">
                    <li>• You violate these Terms</li>
                    <li>• You provide false information</li>
                    <li>• You engage in fraudulent activity</li>
                    <li>• For any reason at our discretion</li>
                  </ul>
                </div>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-semibold text-neutral-900 mb-2">Upon termination:</h3>
                  <ul className="text-neutral-700 text-sm space-y-1">
                    <li>• Your access rights cease immediately</li>
                    <li>• Outstanding orders may be cancelled</li>
                    <li>• Data may be deleted after notice period</li>
                    <li>• Some provisions survive termination</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">⚖️</span>
                Governing Law
              </h2>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-blue-800">
                  These Terms shall be governed by and construed in accordance with the laws of India. 
                  Any disputes arising under or in connection with these Terms shall be subject to the 
                  exclusive jurisdiction of the courts in Sivakasi, Tamil Nadu, India.
                </p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔄</span>
                Changes to Terms
              </h2>
              <div className="bg-neutral-100 rounded-xl p-6">
                <p className="text-neutral-700 mb-3">
                  We reserve the right to modify these Terms at any time. We will notify you of changes by:
                </p>
                <ul className="space-y-2 text-neutral-700">
                  <li>• Posting updated Terms on our website</li>
                  <li>• Sending email notification for material changes</li>
                  <li>• Updating the "Last updated" date</li>
                </ul>
                <p className="text-neutral-700 mt-3">
                  Continued use after changes constitutes acceptance of new Terms.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-accent-50 rounded-xl p-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-4">Contact Information</h2>
              <p className="text-neutral-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              {loading ? (
                <div className="flex items-center justify-center h-20">
                  <div className="text-neutral-600">Loading contact information...</div>
                </div>
              ) : companyData ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Legal Department</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="mr-2">✉️</span>
                        <span className="text-neutral-700">{companyData.email}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">📞</span>
                        <span className="text-neutral-700">{companyData.phone}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Business Address</h3>
                    <div className="text-neutral-700 text-sm">
                      <p>{companyData.companyName} Legal Department</p>
                      <p>{companyData.address.street}</p>
                      <p>{companyData.address.city}, {companyData.address.state} {companyData.address.zipCode}</p>
                      <p>{companyData.address.country}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-neutral-600">Contact information unavailable. Please try again later.</div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;